import { Target, DollarSign, Users, Clock, Award, Headphones, CheckCircle2, ShieldCheck, FileCheck, TrendingUp } from "lucide-react";

const REASONS = [
  {
    icon: Target,
    title: "Specialized Exclusively in US Trademarks",
    description:
      "Unlike general legal service providers, Trademark Officers focuses exclusively on trademark registration in the United States. This singular focus means our team possesses deep, specialized expertise in USPTO procedures, trademark examination standards, and federal trademark law. We stay current with every regulatory change, policy update, and procedural shift at the USPTO, ensuring your application benefits from the most up-to-date knowledge and strategies available.",
  },
  {
    icon: Award,
    title: "98% Application Success Rate",
    description:
      "Our meticulous approach to trademark search, application preparation, and examination response has resulted in a 98% success rate for trademark applications we file. This industry-leading success rate is the direct result of our rigorous pre-filing search process, precise classification of goods and services, and expertly crafted application materials. When you work with Trademark Officers, you can be confident that your application is in the hands of professionals who know how to get it approved.",
  },
  {
    icon: DollarSign,
    title: "Transparent Flat-Fee Pricing",
    description:
      "We believe in complete transparency when it comes to costs. Our flat-fee pricing model means you know exactly what your trademark registration will cost before you begin, with no hidden charges, surprise invoices, or hourly billing. Each of our service packages clearly outlines what is included, so you can choose the level of protection that best fits your budget and needs without worrying about escalating fees.",
  },
  {
    icon: Users,
    title: "Dedicated Case Managers",
    description:
      "Every client who works with Trademark Officers is assigned a dedicated case manager who serves as your single point of contact throughout the entire registration process. Your case manager keeps you informed at every stage, answers your questions promptly, and proactively addresses any issues that arise during examination. This personalized approach ensures you never feel lost in the process and always know the status of your application.",
  },
  {
    icon: Clock,
    title: "Fast and Efficient Processing",
    description:
      "Time is of the essence when it comes to trademark registration. The sooner your application is filed, the sooner you establish priority over others who may be seeking to register similar marks. Our streamlined filing process ensures your application is prepared and submitted as quickly as possible without sacrificing accuracy or thoroughness. We understand the urgency of brand protection and work diligently to minimize delays at every stage.",
  },
  {
    icon: Headphones,
    title: "Ongoing Support and Guidance",
    description:
      "Our relationship with clients does not end when your trademark is registered. We provide ongoing support to help you maintain and enforce your trademark rights, including timely reminders for maintenance filings, guidance on proper trademark use, and assistance with enforcement actions when needed. Our commitment to long-term client success sets us apart from firms that treat trademark registration as a one-time transaction.",
  },
];

export default function WhyChooseUs() {
  return (
    <section id="why-choose-us" data-testid="why-choose-us-section" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left column - Text + Image + Additional content */}
          <div className="lg:sticky lg:top-28">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A] mb-4">
              Why Choose Us
            </span>
            <h2
              data-testid="why-choose-us-heading"
              className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-6"
            >
              Why Businesses Trust Trademark Officers
            </h2>
            <p className="text-[#475569] leading-relaxed mb-8">
              Choosing the right partner for your trademark registration is a critical decision that can impact your brand's future for years to come. At Trademark Officers, we combine deep USPTO expertise, personalized client service, and transparent pricing to deliver an experience that goes far beyond simply filing paperwork. Here is what sets us apart from other trademark service providers.
            </p>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc3NjE5Mjg3Mnww&ixlib=rb-4.1.0&q=85"
                alt="Trademark Officers team of professionals collaborating on trademark strategy"
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Stats below image */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center">
                <div className="font-heading text-2xl font-extrabold text-[#1E3A8A]">98%</div>
                <div className="text-xs text-[#475569] mt-1 font-medium">Approval Rate</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center">
                <div className="font-heading text-2xl font-extrabold text-[#1E3A8A]">1,000+</div>
                <div className="text-xs text-[#475569] mt-1 font-medium">Brands Protected</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center">
                <div className="font-heading text-2xl font-extrabold text-[#1E3A8A]">50</div>
                <div className="text-xs text-[#475569] mt-1 font-medium">States Covered</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center">
                <div className="font-heading text-2xl font-extrabold text-[#1E3A8A]">24hr</div>
                <div className="text-xs text-[#475569] mt-1 font-medium">Response Time</div>
              </div>
            </div>

            {/* Additional content below stats */}
            <div className="mt-6 bg-[#1E3A8A] rounded-lg p-6 text-white">
              <h3 className="font-heading text-lg font-bold mb-3">Our Commitment to Your Brand</h3>
              <p className="text-blue-100 text-sm leading-relaxed mb-4">
                We treat every trademark application as if it were our own brand at stake. Our team conducts rigorous due diligence, prepares meticulously documented filings, and maintains proactive communication throughout the entire registration journey. From first consultation to final certificate, you will always know exactly where your application stands and what comes next.
              </p>
              <div className="space-y-2.5">
                {[
                  { icon: ShieldCheck, text: "Full USPTO compliance guaranteed on every filing" },
                  { icon: FileCheck, text: "Detailed progress updates at every milestone" },
                  { icon: TrendingUp, text: "Strategic guidance to maximize approval odds" },
                  { icon: CheckCircle2, text: "Post-registration monitoring and maintenance support" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2.5">
                    <item.icon className="w-4 h-4 text-blue-300 shrink-0" />
                    <span className="text-sm text-blue-100">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Reason cards */}
          <div className="space-y-5">
            {REASONS.map((reason, i) => (
              <div
                key={reason.title}
                data-testid={`why-choose-card-${i}`}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center shrink-0">
                    <reason.icon className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-[#0F172A] mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-[#475569] text-sm leading-relaxed">{reason.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
